import { Injectable, inject } from '@angular/core';
import { OktaAuthStateService } from '@okta/okta-angular';
import { AuthState } from '@okta/okta-auth-js';
import { CompanyUsers, NotificationSettings, UpdateUserLoginRequest, UpdateUserRoleRequest, User, UserCommentsRequest, UserDetailedSearchViewRequest, UserSynonymsRequest } from '@shared/models/users.model';
import { Observable, filter, map } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { IResponse } from '@shared/models/base/http-response.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http: HttpClient = inject(HttpClient);

  isAuthenticated$: Observable<boolean>;

  constructor(
    public authStateService: OktaAuthStateService,
    public oktaStateService: OktaAuthStateService,
  ) {
    this.isAuthenticated$ = this.oktaStateService.authState$.pipe(
      filter((s: AuthState) => !!s),
      map((s: AuthState) => s.isAuthenticated ?? false),
    );
  }

  getCurrentUserProfile(): Observable<IResponse<User>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<User>>(
      new URL('/user/GetCurrentUserProfile', environment.apiUrl).href,
      { headers },
    );
  }

  checkEmailExists(email: string): Observable<IResponse<boolean>> {
    const params = new HttpParams()
      .set('email', email);

    return this.http.get<IResponse<boolean>>(
      new URL('/user/CheckEmailExists', environment.apiUrl).href,
      { params }
    );
  }

  resetUserPassword(email: string): Observable<IResponse<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<any>>(
      new URL('/user/ResetPassword', environment.apiUrl).href,
      JSON.stringify(email),
      { headers },
    );
  }

  updateUserInfo(user: User): Observable<IResponse<boolean>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<boolean>>(
      new URL('/user/UpdateUser', environment.apiUrl).href,
      JSON.stringify(user),
      { headers },
    );
  }

  updateNotificationSettings(notification: NotificationSettings): Observable<IResponse<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<boolean>>(
      new URL('/user/UpdateUserNotifications', environment.apiUrl).href,
      JSON.stringify(notification),
      { headers },
    );
  }

  updateUserLastLogin(updateUserLoginRequest: UpdateUserLoginRequest): Observable<IResponse<boolean>> {
    return this.http.post<IResponse<boolean>>(
      new URL(`/user/UpdateUserLastLogin`, environment.apiUrl).href,
      updateUserLoginRequest,
    );
  }

  updateUserSynonyms(request: UserSynonymsRequest): Observable<IResponse<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<boolean>>(
      new URL('/user/UpdateUserSynonyms', environment.apiUrl).href,
      JSON.stringify(request),
      { headers },
    );
  }

  updateUserComment(request: UserCommentsRequest): Observable<IResponse<any>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<boolean>>(
      new URL('/user/UpdateUserComments', environment.apiUrl).href,
      JSON.stringify(request),
      { headers },
    );
  }


    updateUserRole(request: UpdateUserRoleRequest): Observable<IResponse<boolean>> {
      return this.http.post<IResponse<boolean>>(
        new URL(`/user/UpdateUserRole`, environment.apiUrl).href,
        request,
      );
    }
    
  updateDetailedSearchView(request: UserDetailedSearchViewRequest): Observable<IResponse<boolean>> {
    return this.http.post<IResponse<boolean>>(
      new URL(`/user/UpdateUserDetailedSearchView`, environment.apiUrl).href,
      request,
    );
  }

}
